<template>
  <v-dialog :model-value="modelValue" max-width="720" scrollable @update:model-value="$emit('update:modelValue', $event)">
    <v-card rounded="lg">
      <v-card-title class="d-flex align-center justify-space-between pr-2">
        <span class="text-h6">编辑变压器保护策略</span>
        <v-btn icon variant="text" @click="$emit('update:modelValue', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-0">
        <div class="px-4 pt-2 d-flex ga-2">
          <v-btn size="small" variant="text">复制</v-btn>
          <v-btn size="small" variant="text">粘贴</v-btn>
        </div>
        <v-alert type="warning" variant="tonal" density="compact" class="ma-4 mb-2" rounded="lg">
          提示：停机切换为其他模式时，时间有延迟
        </v-alert>
        <div class="px-4 pb-4 d-flex flex-column ga-4">
          <v-card
            v-for="seg in segments"
            :key="seg.id"
            variant="outlined"
            rounded="lg"
            class="seg-card"
          >
            <div class="seg-head pa-3 d-flex flex-wrap align-center ga-2">
              <v-btn icon size="small" variant="text" @click="seg.expanded = !seg.expanded">
                <v-icon>{{ seg.expanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
              </v-btn>
              <span class="text-body-2">时段:</span>
              <v-text-field v-model="seg.t0" density="compact" variant="outlined" hide-details prepend-inner-icon="mdi-clock-outline" style="max-width: 120px" />
              <span>至</span>
              <v-text-field v-model="seg.t1" density="compact" variant="outlined" hide-details prepend-inner-icon="mdi-clock-outline" style="max-width: 120px" />
              <v-chip size="small" color="orange" variant="flat">{{ seg.tag }}</v-chip>
              <v-spacer />
              <v-btn size="small" variant="text">复制</v-btn>
              <v-btn size="small" variant="text">粘贴</v-btn>
              <v-btn icon size="small" variant="text" color="error" @click="removeSegment(seg.id)">
                <v-icon size="20">mdi-delete-outline</v-icon>
              </v-btn>
            </div>
            <v-expand-transition>
              <div v-show="seg.expanded" class="px-3 pb-3">
                <v-card
                  v-for="rule in seg.rules"
                  :key="rule.id"
                  class="rule-block pa-3 mb-2"
                  variant="flat"
                  rounded="lg"
                >
                  <div class="d-flex align-center justify-space-between mb-2">
                    <div class="d-flex align-center ga-2">
                      <span class="text-body-2">动作:</span>
                      <v-select
                        v-model="rule.action"
                        :items="actionItems"
                        density="compact"
                        variant="outlined"
                        hide-details
                        placeholder="请选择"
                        style="min-width: 160px"
                      />
                    </div>
                    <v-btn icon size="small" variant="text" color="error" @click="removeRule(seg, rule.id)">
                      <v-icon size="20">mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                  <v-row dense>
                    <v-col cols="12">
                      <div class="text-caption mb-1">SOC 区间</div>
                      <v-range-slider
                        v-model="rule.soc"
                        strict
                        color="primary"
                        thumb-label="always"
                        density="compact"
                        hide-details
                      />
                      <div class="d-flex justify-space-between text-caption text-medium-emphasis">
                        <span>SOC上限: {{ rule.soc[1].toFixed(1) }} %</span>
                        <span>SOC下限: {{ rule.soc[0].toFixed(1) }} %</span>
                      </div>
                    </v-col>
                    <v-col cols="12">
                      <div class="text-caption mb-1">变压器容量区间</div>
                      <v-range-slider
                        v-model="rule.cap"
                        strict
                        color="orange"
                        thumb-label="always"
                        density="compact"
                        hide-details
                      />
                      <div class="d-flex justify-space-between text-caption text-medium-emphasis">
                        <span>变压器容量上限: {{ rule.cap[1].toFixed(1) }} %</span>
                        <span>变压器容量下限: {{ rule.cap[0].toFixed(1) }} %</span>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="d-flex align-center ga-2">
                        <span class="text-body-2 text-no-wrap">持续时间:</span>
                        <v-text-field v-model.number="rule.duration" type="number" density="compact" variant="outlined" hide-details style="max-width: 100px" />
                        <span class="text-caption">s</span>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="d-flex align-center ga-2">
                        <span class="text-body-2 text-no-wrap">功率模式:</span>
                        <v-select v-model="rule.powerMode" :items="['额定', '限功率']" density="compact" variant="outlined" hide-details style="max-width: 140px" />
                      </div>
                    </v-col>
                    <v-col cols="12">
                      <div class="d-flex align-start ga-1 text-caption text-orange-darken-2">
                        <v-icon size="16" class="mt-0">mdi-alert-outline</v-icon>
                        <span>按照变压器负载率上限为目标</span>
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
                <v-btn variant="text" size="small" color="primary" prepend-icon="mdi-plus" class="text-none" @click="addRule(seg)">
                  增加规则
                </v-btn>
              </div>
            </v-expand-transition>
          </v-card>
          <v-btn variant="tonal" color="primary" prepend-icon="mdi-plus" class="text-none align-self-start" @click="addSegment">
            增加时段
          </v-btn>
        </div>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn variant="text" rounded="lg" @click="$emit('update:modelValue', false)">取消</v-btn>
        <v-btn color="#2F59EB" variant="flat" rounded="lg" @click="confirm">保存并下发</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, reactive } from 'vue'

defineProps({
  modelValue: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'saved'])

const actionItems = ['需量保护', 'SOC 保护', '温度保护', '停机']

let segId = 2
let ruleId = 10

const segments = reactive([
  {
    id: 1,
    t0: '00:00',
    t1: '08:00',
    tag: '尖',
    expanded: true,
    rules: [
      {
        id: 1,
        action: null,
        soc: [60.1, 100],
        cap: [0, 100],
        duration: 1,
        powerMode: '额定',
      },
    ],
  },
])

function addSegment() {
  segId += 1
  segments.push({
    id: segId,
    t0: '08:00',
    t1: '12:00',
    tag: '峰',
    expanded: true,
    rules: [
      {
        id: ++ruleId,
        action: null,
        soc: [20, 90],
        cap: [0, 100],
        duration: 1,
        powerMode: '额定',
      },
    ],
  })
}

function removeSegment(id) {
  const i = segments.findIndex((s) => s.id === id)
  if (i >= 0) segments.splice(i, 1)
}

function addRule(seg) {
  ruleId += 1
  seg.rules.push({
    id: ruleId,
    action: null,
    soc: [0, 100],
    cap: [0, 100],
    duration: 1,
    powerMode: '额定',
  })
}

function removeRule(seg, rid) {
  seg.rules = seg.rules.filter((r) => r.id !== rid)
}

function confirm() {
  emit('saved', JSON.parse(JSON.stringify(segments)))
  emit('update:modelValue', false)
}
</script>

<style scoped>
.seg-card {
  border-color: #e0e4ed !important;
}
.rule-block {
  background: #f4f5f8;
  border: 1px solid #e8eaef;
}
</style>
